import React, { useEffect, useState } from 'react';
import { SurveyConfig, SurveyAnalytics, SurveyQuestion, SurveySubmission } from '../../types/PluginTypes';
import { SurveyStorage, generateAnalytics } from '../../utils/SurveyUtils';

interface SurveyResultsProps {
  config: SurveyConfig;
}

const SurveyResults: React.FC<SurveyResultsProps> = ({ config }) => {
  const [analytics, setAnalytics] = useState<SurveyAnalytics | null>(null);
  const [submissions, setSubmissions] = useState<SurveySubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ID dérivé du titre (comme dans le plugin principal)
  const surveyId = (config.title || 'survey').toLowerCase().replace(/\s+/g, '_');

  useEffect(() => {
    loadData();
  }, [config.title]);

  const loadData = async () => {
    setLoading(true);
    try {
      // ✅ CORRECTION : on utilise 'await' car getSubmissions retourne une Promise
      const data = await SurveyStorage.getSubmissions(surveyId);
      
      setSubmissions(data);

      if (data && data.length > 0) {
        const stats = generateAnalytics(data, config.questions || []);
        setAnalytics(stats);
      } else {
        setAnalytics(null);
      }
    } catch (err) {
      console.error(err);
      setError("Impossible de charger les résultats pour l'instant.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center p-5"><div className="spinner-border text-primary" role="status"></div><div className="mt-2 text-muted">Chargement des statistiques...</div></div>;
  
  if (error) return <div className="alert alert-danger m-3">{error}</div>;

  if (!analytics || analytics.totalSubmissions === 0) {
    return (
      <div className="text-center p-5 bg-light rounded m-3">
        <div className="display-4 text-muted mb-3">📊</div>
        <h5>Aucune donnée disponible</h5>
        <p className="text-muted">Les résultats s'afficheront ici une fois que les utilisateurs auront répondu au questionnaire "{config.title}".</p>
        <button className="btn btn-outline-primary btn-sm mt-2" onClick={loadData}>🔄 Actualiser</button>
      </div>
    );
  }

  // Sous-fonction pour le rendu spécifique selon le type de question
  const renderQuestionResult = (question: SurveyQuestion, stats: any) => {
    // 1. Types graphiques (Barres)
    if (['radio', 'checkbox', 'select', 'rating', 'scale'].includes(question.type)) {
      const total = stats.responseCount || 1;
      const sortedKeys = Object.keys(stats.responseDistribution || {}).sort((a,b) => (stats.responseDistribution[b] - stats.responseDistribution[a]));

      return (
        <div>
          {sortedKeys.map((label) => {
            const count = stats.responseDistribution[label];
            const percent = ((count / total) * 100).toFixed(1);
            return (
              <div key={label} className="mb-2">
                <div className="d-flex justify-content-between small mb-1">
                  <span>{label || "(Vide)"}</span>
                  <span className="fw-bold">{count} ({percent}%)</span>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div 
                    className="progress-bar bg-primary" 
                    style={{ width: `${percent}%`, opacity: 0.8 }} 
                    role="progressbar" 
                  />
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // 2. Types textuels (Liste des dernières réponses)
    const textResponses = submissions
        .map(sub => sub.responses.find(r => r.questionId === question.id)?.value)
        .filter(val => val && String(val).trim() !== '')
        .slice(0, 50); // On limite à 50 réponses pour l'affichage brut

    return (
      <div>
        <div className="mb-2 small text-muted">
           {stats.responseCount} réponse(s) • Temps moyen : {stats.averageTimeSpent.toFixed(1)}s
        </div>
        <div className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {textResponses.length > 0 ? (
                <ul className="list-unstyled mb-0">
                    {textResponses.map((text, i) => (
                        <li key={i} className="mb-2 border-bottom pb-2 last-no-border small text-break">
                            {String(text)}
                        </li>
                    ))}
                </ul>
            ) : (
                <span className="text-muted fst-italic small">Aucune réponse textuelle.</span>
            )}
        </div>
      </div>
    );
  };

  return (
    <div className="survey-results p-3">
      {/* En-tête avec métriques globales */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card h-100 border-0 shadow-sm bg-primary text-white">
            <div className="card-body text-center">
              <h6 className="opacity-75">Réponses totales</h6>
              <div className="display-5 fw-bold">{analytics.totalSubmissions}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body text-center">
              <h6 className="text-muted">Taux d'achèvement</h6>
              <div className="display-5 fw-bold text-success">
                {(analytics.completionRate).toFixed(0)}%
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body text-center">
              <h6 className="text-muted">Temps moyen</h6>
              <div className="display-5 fw-bold text-info">
                {formatTime(analytics.averageTimeSpent)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
           <h5 className="mb-0">Détail par question</h5>
           <button className="btn btn-sm btn-outline-secondary" onClick={loadData}>🔄 Actualiser</button>
      </div>

      {/* Liste des résultats par question */}
      <div className="d-flex flex-column gap-4">
        {config.questions?.map((question, index) => {
          const stats = analytics.questionAnalytics[question.id];
          if (!stats) return null;

          return (
            <div key={question.id} className="card border shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between">
                <strong>{index + 1}. {question.title}</strong>
                <span className="badge bg-light text-dark border">{stats.responseCount} réponses</span>
              </div>
              <div className="card-body">
                {renderQuestionResult(question, stats)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helper format temps
const formatTime = (seconds: number) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
};

// ✅ L'export par défaut est ici, ce qui résout l'erreur dans SurveyPluginModular
export default SurveyResults;